import { EntityImage, formatCurrencyWithConditionalDecimals } from '@urbanx/agx-ui-components';
import {
  ContractDateToUse,
  StatisticsTimePeriod,
  StatisticsType,
  useStaffGetLeaderboardStatistics,
} from '@urbanx/agx.rex.client';
import {
  AgxDatePicker,
  AgxDropdown,
  AgxEmptyState,
  AgxSpacer,
  AgxTable,
  AgxTypography,
  Breakpoints,
  DropdownOption,
  IColumnConfig,
  toDateOnlyString,
  toExclusiveDate,
  useWindowSize,
} from '@urbanx/agx.ui-components2';
import CleanPanel from 'components/cleanPanel/CleanPanel';
import LoadingState from 'components/loadingState/LoadingState';
import { useMemo, useState } from 'react';

const entityTypeOptions: DropdownOption[] = [
  { label: 'Individual/Team', value: StatisticsType.IndividualOrTeam },
  { label: 'Agency', value: StatisticsType.Agency },
];

const contractTypeOptions: DropdownOption[] = [
  { label: 'Written', value: ContractDateToUse.Written },
  { label: 'Unconditional', value: ContractDateToUse.Unconditional },
  { label: 'Settled', value: ContractDateToUse.Settled },
];

const dateTypeOptions: DropdownOption[] = [
  { label: 'Month', value: StatisticsTimePeriod.Month },
  { label: 'Quarter', value: StatisticsTimePeriod.Quarter },
  { label: 'Year', value: StatisticsTimePeriod.Annual },
  { label: 'Last 12 months', value: StatisticsTimePeriod.Previous },
  { label: 'Custom', value: StatisticsTimePeriod.Custom },
];

const LeaderboardPage = () => {
  const [selectedEntityType, setSelectedEntityType] = useState<StatisticsType>(
    StatisticsType.IndividualOrTeam
  );

  const [selectedContractType, setSelectedContractType] = useState<ContractDateToUse>(
    ContractDateToUse.Unconditional
  );

  const [selectedDateType, setSelectedDateType] = useState<StatisticsTimePeriod>(
    StatisticsTimePeriod.Month
  );

  const [customStartDate, setCustomStartDate] = useState<Date | null>(
    () => new Date(new Date().setFullYear(new Date().getFullYear() - 1))
  );
  const [customEndDateInclusive, setCustomEndDateInclusive] = useState<Date | null>(
    () => new Date()
  );

  const customFromDate =
    selectedDateType === StatisticsTimePeriod.Custom && customStartDate
      ? toDateOnlyString(customStartDate)
      : undefined;

  const customToDateExclusive =
    selectedDateType === StatisticsTimePeriod.Custom && customEndDateInclusive
      ? toExclusiveDate(customEndDateInclusive)
      : undefined;

  const { data: leaderboardStats, isPending: isLeaderboardStatsPending } =
    useStaffGetLeaderboardStatistics(
      selectedDateType,
      selectedContractType,
      customFromDate,
      customToDateExclusive,
      selectedEntityType,
      true
    );

  const entityColumnName = useMemo(() => {
    return selectedEntityType === StatisticsType.Agency ? 'Agency' : 'Agent';
  }, [selectedEntityType]);

  const columns: (string | IColumnConfig)[] = useMemo(
    () => [entityColumnName, { name: 'GCI', align: 'right' }],
    [entityColumnName]
  );

  const rows = useMemo(() => {
    if (!leaderboardStats?.entityRankings) return [];

    return leaderboardStats.entityRankings.map((commissionRanking) => {
      return {
        [entityColumnName]: (
          <AgxSpacer direction="horizontal" gap="spacing-lg" fill contentAlignment="center">
            <AgxTypography
              type="body"
              size="medium"
              id={`leaderboard-rank-${commissionRanking.rank}`}
              style={{
                minWidth: '20px',
                textAlign: 'center',
              }}
              color="var(--text-primary)"
              numberFont
            >
              {commissionRanking.rank}
            </AgxTypography>
            <AgxSpacer direction="horizontal" gap="spacing-lg" fill contentAlignment="center">
              <EntityImage
                photoUri={commissionRanking.photoUri ?? null}
                backgroundColour={commissionRanking.backgroundColour ?? null}
                backgroundTextColour={commissionRanking.backgroundTextColour ?? null}
                name={commissionRanking.name}
                addRingToImage={entityColumnName === 'Agent'}
                size={40}
              />
              <AgxTypography
                id={`leaderboard-name-${commissionRanking.name}`}
                type="body"
                size="medium"
                color="var(--text-primary)"
              >
                {commissionRanking.name}
              </AgxTypography>
            </AgxSpacer>
          </AgxSpacer>
        ),
        ['GCI']: (
          <AgxTypography
            id={`leaderboard-commission-${commissionRanking.rank}`}
            type="body"
            size="medium"
            color="var(--text-primary)"
            numberFont
          >
            {formatCurrencyWithConditionalDecimals(commissionRanking.commission ?? 0, 0)}
          </AgxTypography>
        ),
      };
    });
  }, [leaderboardStats, entityColumnName]);

  const windowSize = useWindowSize();

  return (
    <CleanPanel>
      <AgxSpacer direction="vertical" fill gap="spacing-2xl" padding="spacing-2xl">
        <AgxSpacer
          direction={windowSize === Breakpoints.Mobile ? 'vertical' : 'horizontal'}
          gap={'spacing-2xl'}
          fill
        >
          <AgxSpacer direction="horizontal" gap={'spacing-2xl'} fill>
            <AgxDropdown
              id={'entityTypeSelector'}
              options={entityTypeOptions}
              onChange={(value) => setSelectedEntityType(value as StatisticsType)}
              selected={selectedEntityType}
              label="Leaderboard Type"
              hideOptionalText
            />
            <AgxDropdown
              id={'contractTypeSelector'}
              options={contractTypeOptions}
              onChange={(value) => setSelectedContractType(value as ContractDateToUse)}
              selected={selectedContractType}
              label="Contract Type"
              hideOptionalText
            />
          </AgxSpacer>
          <AgxDropdown
            id={'dateTypeSelector'}
            options={dateTypeOptions}
            onChange={(value) => setSelectedDateType(value as StatisticsTimePeriod)}
            selected={selectedDateType}
            label="Date Period"
            hideOptionalText
          />
        </AgxSpacer>
        <AgxSpacer direction="horizontal" gap={'spacing-2xl'} fill>
          {selectedDateType === StatisticsTimePeriod.Custom && (
            <>
              <AgxDatePicker
                id={'customDatePeriodPicker'}
                value={customStartDate}
                onChange={setCustomStartDate}
                optionalText=""
                stretch
                label="From"
              />
              <AgxDatePicker
                id={'customEndDatePeriodPicker'}
                value={customEndDateInclusive}
                onChange={setCustomEndDateInclusive}
                optionalText=""
                stretch
                label="To"
              />
            </>
          )}
        </AgxSpacer>
        <AgxSpacer direction="vertical" fill>
          <AgxTable variant="default" columns={columns} rows={rows} />
          {isLeaderboardStatsPending && <LoadingState />}
          {!isLeaderboardStatsPending && leaderboardStats?.entityRankings?.length === 0 && (
            <AgxEmptyState variant="minimal" label="No rankings found" />
          )}
        </AgxSpacer>
      </AgxSpacer>
    </CleanPanel>
  );
};

export default LeaderboardPage;
